import { CURRENCY } from '../config/settings.js';
import { findByCode, DEFAULT_COUNTRY } from './countries.js';

/** @param {number} n */
export const formatPrice = (n) => `${n.toFixed(2)} ${CURRENCY}`;

/**
 * Normalize a phone number to E.164-ish for UA (+380XXXXXXXXX).
 * Accepts user input like "067 123 45 67", "+38 067 ...", "0671234567".
 */
export function normalizeUaPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith('380')) return '+' + digits;
  if (digits.startsWith('0')) return '+38' + digits;
  return '+' + digits;
}

/**
 * Combine a local phone number with its country-code selection to produce a
 * full E.164 international number ready for Shopify (and any other API that
 * insists on the `+<dial><local>` form).
 *
 *   formatPhoneE164("682162166", "UA")  → "+380682162166"
 *   formatPhoneE164("0682162166", "UA") → "+380682162166"   (drops trunk 0)
 *   formatPhoneE164("+380682162166", "UA") → "+380682162166" (preserves)
 *   formatPhoneE164("",  "UA") → ""
 */
export function formatPhoneE164(localPhone, countryCode = DEFAULT_COUNTRY) {
  const raw = String(localPhone || '').trim();
  if (!raw) return '';
  const startsWithPlus = raw.startsWith('+');
  // Strip all non-digits AND any leading trunk zeros that slipped through.
  let digits = raw.replace(/\D/g, '').replace(/^0+/, '');
  if (!digits) return '';

  const dial = findByCode(countryCode)?.dial || '';
  // Already-international forms are honored verbatim.
  if (startsWithPlus) return '+' + digits;
  // The user might have typed the dial code with no '+': "380682..." → "+380682..."
  if (dial && digits.startsWith(dial)) return '+' + digits;
  return '+' + dial + digits;
}

/**
 * Pick the right Ukrainian plural form for a count.
 * Ukrainian has three: 1 товар / 2-4 товари / 5-20 товарів, and the pattern
 * repeats by the last digit (21 товар, 22 товари, 25 товарів) except for the
 * teens (11-14 always take the "many" form).
 *
 *   pluralUa(1,  'товар', 'товари', 'товарів')  -> 'товар'
 *   pluralUa(3,  ...)                            -> 'товари'
 *   pluralUa(5,  ...)                            -> 'товарів'
 *   pluralUa(21, ...)                            -> 'товар'
 *   pluralUa(12, ...)                            -> 'товарів'
 */
export function pluralUa(n, one, few, many) {
  const abs = Math.abs(Number(n) || 0);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}

/** Escape user-supplied text before inserting into innerHTML. */
export function escapeHtml(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
